/**
 * Image pipeline for receipts: downscale to ≤ 2000 px JPEG (q .87) before
 * upload, and make 400 px thumbnails for the inbox list.
 *
 * `createImageBitmap(file, { resizeWidth, resizeHeight, resizeQuality: 'high' })`
 * decodes, applies EXIF orientation (the default `imageOrientation`) and
 * resizes in one step. The canvas we draw on stays under the iOS area limit
 * (16.7 MP). PDFs pass through untouched; their thumbnail is the first page
 * rendered with pdf.js, or a neutral placeholder when that is unavailable.
 */
export const DEFAULT_MAX_EDGE = 2000;
export const DEFAULT_JPEG_QUALITY = 0.87;
export const THUMBNAIL_MAX_EDGE = 400;
export const THUMBNAIL_JPEG_QUALITY = 0.8;
/** Safari/iOS refuses canvases above ~16.7 million pixels. */
export const MAX_CANVAS_AREA = 16_777_216;

export const PDF_MIME = 'application/pdf';
const IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/avif', 'image/heic', 'image/heif']);

export function isPdf(blob: Blob): boolean {
  return blob.type === PDF_MIME;
}

export function isImage(blob: Blob): boolean {
  return IMAGE_MIMES.has(blob.type);
}

/**
 * Target size so the longest edge is ≤ maxEdge and the area ≤ maxArea,
 * never upscaling. Pure; returns integers ≥ 1.
 */
export function fitWithin(width: number, height: number, maxEdge: number, maxArea: number = MAX_CANVAS_AREA): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 1, height: 1 };
  let scale = Math.min(1, maxEdge / Math.max(width, height));
  const area = width * scale * (height * scale);
  if (area > maxArea) scale = Math.min(scale, Math.sqrt(maxArea / (width * height)));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** True when the browser offers everything the shrink path needs. */
export function canProcessImages(): boolean {
  return typeof createImageBitmap === 'function' && (typeof OffscreenCanvas === 'function' || typeof document !== 'undefined');
}

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;

function makeCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas === 'function') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

async function canvasToJpeg(canvas: AnyCanvas, quality: number): Promise<Blob> {
  if ('convertToBlob' in canvas) return canvas.convertToBlob({ type: 'image/jpeg', quality });
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))), 'image/jpeg', quality);
  });
}

async function decodeSize(file: Blob): Promise<{ width: number; height: number }> {
  const probe = await createImageBitmap(file);
  try {
    return { width: probe.width, height: probe.height };
  } finally {
    probe.close();
  }
}

/** Decode + resize + re-encode as JPEG on a white background (kills alpha). */
async function encodeResized(file: Blob, maxEdge: number, quality: number): Promise<Blob> {
  const size = await decodeSize(file);
  const target = fitWithin(size.width, size.height, maxEdge);
  const bitmap = await createImageBitmap(file, { resizeWidth: target.width, resizeHeight: target.height, resizeQuality: 'high' });
  try {
    const canvas = makeCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
    if (!ctx) throw new Error('2d context unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, bitmap.width, bitmap.height);
    ctx.drawImage(bitmap, 0, 0);
    return await canvasToJpeg(canvas, quality);
  } finally {
    bitmap.close();
  }
}

/**
 * Downscale a photo to ≤ `maxEdge` px JPEG. PDFs and unknown types are
 * returned as-is; so is any image when the browser cannot process images
 * (the upload still works, only bigger). Never throws for a decodable file.
 */
export async function shrinkImage(file: Blob, o: { maxEdge?: number; quality?: number } = {}): Promise<Blob> {
  if (isPdf(file) || !isImage(file)) return file;
  if (!canProcessImages()) return file;
  const maxEdge = o.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = o.quality ?? DEFAULT_JPEG_QUALITY;
  try {
    return await encodeResized(file, maxEdge, quality);
  } catch {
    // Undecodable or too large for this device's canvas: keep the original.
    return file;
  }
}

/** Grey page with a folded corner and "PDF"; used when a PDF page cannot be rendered. */
export function placeholderThumbnail(label = 'PDF'): Blob {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="400" viewBox="0 0 300 400">` +
    `<rect width="300" height="400" fill="#f1f1f1"/><path d="M40 20h160l60 60v300H40z" fill="#fff" stroke="#c9c9c9" stroke-width="2"/>` +
    `<path d="M200 20v60h60" fill="#e6e6e6" stroke="#c9c9c9" stroke-width="2"/>` +
    `<text x="150" y="230" font-family="system-ui, sans-serif" font-size="56" font-weight="700" fill="#8a8a8a" text-anchor="middle">${label}</text></svg>`;
  return new Blob([svg], { type: 'image/svg+xml' });
}

async function renderPdfThumbnail(file: Blob, maxEdge: number): Promise<Blob> {
  const pdfjs = await import('pdfjs-dist');
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
  }
  const data = new Uint8Array(await file.arrayBuffer());
  const task = pdfjs.getDocument({ data });
  const doc = await task.promise;
  try {
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const target = fitWithin(base.width, base.height, maxEdge);
    const viewport = page.getViewport({ scale: target.width / base.width });
    const canvas = makeCanvas(Math.ceil(viewport.width), Math.ceil(viewport.height));
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
    if (!ctx) throw new Error('2d context unavailable');
    await page.render({ canvas: canvas as HTMLCanvasElement, canvasContext: ctx, viewport }).promise;
    return await canvasToJpeg(canvas, THUMBNAIL_JPEG_QUALITY);
  } finally {
    await task.destroy();
  }
}

/**
 * A ≤ `maxEdge` px JPEG thumbnail. Images go through the same resize path;
 * PDFs render page 1 (falls back to a placeholder). Always resolves.
 */
export async function makeThumbnail(file: Blob, maxEdge: number = THUMBNAIL_MAX_EDGE): Promise<Blob> {
  if (isPdf(file)) {
    if (!canProcessImages()) return placeholderThumbnail();
    try {
      return await renderPdfThumbnail(file, maxEdge);
    } catch {
      return placeholderThumbnail();
    }
  }
  if (!isImage(file) || !canProcessImages()) return placeholderThumbnail('FIL');
  try {
    return await encodeResized(file, maxEdge, THUMBNAIL_JPEG_QUALITY);
  } catch {
    return placeholderThumbnail('BILD');
  }
}
